'use client';

import React, { ComponentPropsWithoutRef, FC, useRef, useState } from 'react';
import { cn } from '@wwe-portal/ui/lib/lib';
import { ClickableDiv } from '@wwe-portal/ui/components/button';
import { useMotionValue, motion, useMotionTemplate } from 'motion/react';
import { Slot } from '@radix-ui/react-slot';

const defaultOptions = {
  maxRotateDeg: 15,
  translateZRatio: 10,
  mouseLeaveTimeout: 500,
  perspective: 600,
};

export interface MouseBasedParallaxProps
  extends ComponentPropsWithoutRef<'div'> {
  asChild?: boolean;
  enabled: boolean;
  options?: {
    maxRotateDeg?: number;
    translateZRatio?: number;
    mouseLeaveTimeout?: number;
    perspective?: number;
  };
  containerRef?: React.RefObject<HTMLDivElement | null>;
}

export const MouseBasedParallax: FC<MouseBasedParallaxProps> = ({
  children,
  enabled,
  options: overrideOptions,
  className,
  asChild,
  style: overrideStyle,
  containerRef: overrideContainerRef,
  ...props
}) => {
  const options = {
    ...defaultOptions,
    ...overrideOptions,
  };
  const innerContainerRef = useRef<HTMLDivElement>(null);
  const containerRef = overrideContainerRef ?? innerContainerRef;
  const transformRef = useRef<HTMLDivElement>(null);

  const [isMouseEntered, setIsMouseEntered] = useState(false);

  const rotateX = useMotionValue(0);
  const rotateY = useMotionValue(0);
  const translateZ = useMotionValue(0);

  const mouseLeaveTimeout = useRef<NodeJS.Timeout | null>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (mouseLeaveTimeout.current) {
      clearTimeout(mouseLeaveTimeout.current);
    }
    if (!containerRef.current || !enabled) return;

    const { left, top, width, height } =
      containerRef.current.getBoundingClientRect();

    const clientX = e.clientX;
    const clientY = e.clientY;
    const halfWidth = width / 2;
    const halfHeight = height / 2;
    const dx = clientX - left - halfWidth;
    const dy = clientY - top - halfHeight;

    const x = (dx / width) * 2 * -options.maxRotateDeg;
    const y = (dy / width) * 2 * options.maxRotateDeg;
    rotateX.set(x);
    rotateY.set(y);
    translateZ.set(width / options.translateZRatio);
  };

  const handleMouseEnter = () => {
    if (mouseLeaveTimeout.current) {
      clearTimeout(mouseLeaveTimeout.current);
    }
    setIsMouseEntered(true);
  };

  const handleMouseLeave = () => {
    if (mouseLeaveTimeout.current) {
      clearTimeout(mouseLeaveTimeout.current);
    }
    mouseLeaveTimeout.current = setTimeout(() => {
      rotateX.set(0);
      rotateY.set(0);
      translateZ.set(0);
      setIsMouseEntered(false);
    }, options.mouseLeaveTimeout);
  };

  const Comp = asChild ? Slot : 'div';

  return (
    <Comp
      style={{
        perspective: `${options.perspective}px`,
        ...overrideStyle,
      }}
      className={cn('h-full w-full', className)}
      {...props}
    >
      <ClickableDiv
        ref={containerRef}
        className={cn('h-full w-full duration-200 ease-linear')}
        style={{
          transformStyle: 'preserve-3d',
        }}
      >
        <div
          className={cn(
            'h-full w-full [transform-style:preserve-3d] [&>*]:[transform-style:preserve-3d]'
          )}
        >
          <motion.div
            ref={transformRef}
            className={cn(
              'pointer-events-auto relative h-full w-full transition duration-200 ease-linear',
              {
                'drop-shadow-fx': isMouseEntered,
              }
            )}
            onMouseEnter={handleMouseEnter}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            style={{
              willChange: 'transform, filter',
              transform: useMotionTemplate`
              rotateY(${rotateX}deg) rotateX(${rotateY}deg) translateZ(${translateZ}px)
              `,
            }}
          >
            {children}
          </motion.div>
        </div>
      </ClickableDiv>
    </Comp>
  );
};
